'use client'

import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import * as stylex from '@stylexjs/stylex'
import type { ReactElement, ReactNode, RefObject } from 'react'

import {
  breakpoints,
  colors,
  controlSizes,
  elevation,
  fonts,
  fontSizes,
  layers,
  lineHeights,
  radii,
  sizes,
  spacing,
} from '../tokens/tokens.stylex'

const styles = stylex.create({
  backdrop: {
    backgroundColor: colors.dialogBackdrop,
    inset: 0,
    position: 'fixed',
    zIndex: layers.backdrop,
  },
  viewport: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    inset: 0,
    justifyContent: 'center',
    padding: spacing.space16,
    position: 'fixed',
    zIndex: layers.modal,
  },
  popup: {
    backgroundColor: colors.background,
    borderColor: colors.overlayBorder,
    borderRadius: radii.dialog,
    borderStyle: 'solid',
    borderWidth: 1,
    boxShadow: elevation.dialog,
    boxSizing: 'border-box',
    color: colors.foreground,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: fonts.sans,
    gap: spacing.space16,
    maxHeight: 'calc(100dvh - 32px)',
    maxWidth: '100%',
    outline: 'none',
    overflowY: 'auto',
    padding: { default: spacing.space24, [breakpoints.compact]: spacing.space16 },
    width: 800,
  },
  wide: {
    width: 'min(1100px, calc(100vw - 32px), calc((100dvh - 96px) * 16 / 9))',
  },
  // The page scrolls inside the sheet so its close control remains visible.
  article: { gap: 0, overflowY: 'hidden', padding: 0, position: 'relative', width: sizes.articleDialog },
  articlePage: {
    minHeight: 0,
    outline: { default: 'none', ':focus-visible': `2px solid ${colors.foreground}` },
    outlineOffset: -2,
    overflowY: 'auto',
    paddingBottom: { default: spacing.space72, [breakpoints.compact]: spacing.space48 },
    paddingInline: { default: spacing.space48, [breakpoints.compact]: spacing.space24 },
    paddingTop: { default: spacing.space56, [breakpoints.compact]: spacing.space48 },
  },
  articleTitle: {
    fontSize: { default: fontSizes.pageTitleWide, [breakpoints.compact]: fontSizes.pageTitle },
    letterSpacing: '-1px',
    lineHeight: 1.2,
    paddingInlineEnd: { default: 0, [breakpoints.compact]: spacing.space24 },
  },
  articleClose: {
    backgroundColor: { default: colors.background, ':hover': colors.hover },
    fontSize: 22,
    height: controlSizes.compact,
    marginBlock: 0,
    marginInlineEnd: 0,
    position: 'absolute',
    right: spacing.space12,
    top: spacing.space12,
    width: controlSizes.compact,
  },
  articleBody: { marginTop: spacing.space14 },
  header: {
    alignItems: 'center',
    display: 'flex',
    gap: spacing.space12,
    justifyContent: 'space-between',
  },
  heading: { display: 'grid', gap: spacing.space8, minWidth: 0 },
  title: {
    fontFamily: fonts.interface,
    fontSize: fontSizes.dialogTitle,
    fontWeight: 600,
    lineHeight: lineHeights.dialogTitle,
    margin: 0,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  description: {
    color: colors.secondary,
    fontSize: fontSizes.reading,
    lineHeight: lineHeights.reading,
    margin: 0,
    overflowWrap: 'anywhere',
  },
  close: {
    alignItems: 'center',
    backgroundColor: { default: 'transparent', ':hover': colors.hover },
    borderRadius: radii.small,
    borderWidth: 0,
    color: { default: colors.secondary, ':hover': colors.foreground },
    cursor: 'pointer',
    display: 'flex',
    flexShrink: 0,
    fontFamily: fonts.sans,
    fontSize: fontSizes.body,
    height: 28,
    justifyContent: 'center',
    lineHeight: 1,
    marginBlock: '-6px',
    marginInlineEnd: '-8px',
    outline: { default: 'none', ':focus-visible': `2px solid ${colors.foreground}` },
    outlineOffset: 2,
    padding: 0,
    width: 28,
  },
  body: { fontSize: fontSizes.reading, lineHeight: lineHeights.reading },
})

export type DialogProps = {
  children: ReactNode
  description?: string | undefined
  finalFocusRef?: RefObject<HTMLElement | null> | undefined
  initialFocusRef?: RefObject<HTMLElement | null> | undefined
  onOpenChange(open: boolean): void
  open: boolean
  size?: 'default' | 'wide' | 'article'
  title: string
  trigger?: ReactElement
}

/**
 * Use for a named modal task; choose Popover for contextual non-modal content. Applications supply controlled
 * visibility and decide whether pending work permits a close request. The dialog contains focus and returns it
 * to the invoker or supplied final-focus ref. Portals inherit the document theme. Status: supported.
 */
export function Dialog({
  children,
  description,
  finalFocusRef,
  initialFocusRef,
  onOpenChange,
  open,
  size = 'default',
  title,
  trigger,
}: DialogProps) {
  return (
    <BaseDialog.Root open={open} onOpenChange={(next) => onOpenChange(next)}>
      {trigger && <BaseDialog.Trigger render={trigger} />}
      <BaseDialog.Portal>
        <BaseDialog.Backdrop {...stylex.props(styles.backdrop)} />
        <BaseDialog.Viewport {...stylex.props(styles.viewport)}>
          <BaseDialog.Popup
            initialFocus={initialFocusRef}
            finalFocus={finalFocusRef}
            {...stylex.props(styles.popup, size === 'wide' && styles.wide, size === 'article' && styles.article)}
          >
            {size === 'article' ? (
              <>
                <BaseDialog.Close aria-label="Close dialog" {...stylex.props(styles.close, styles.articleClose)}>
                  ×
                </BaseDialog.Close>
                {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- The named article region needs focus for keyboard scrolling. */}
                <section aria-label={title} tabIndex={0} {...stylex.props(styles.articlePage)}>
                  <BaseDialog.Title {...stylex.props(styles.title, styles.articleTitle)}>{title}</BaseDialog.Title>
                  {description && (
                    <BaseDialog.Description {...stylex.props(styles.description)}>{description}</BaseDialog.Description>
                  )}
                  <div {...stylex.props(styles.articleBody)}>{children}</div>
                </section>
              </>
            ) : (
              <>
                <div {...stylex.props(styles.heading)}>
                  <header {...stylex.props(styles.header)}>
                    <BaseDialog.Title {...stylex.props(styles.title)}>{title}</BaseDialog.Title>
                    <BaseDialog.Close aria-label="Close dialog" {...stylex.props(styles.close)}>
                      ×
                    </BaseDialog.Close>
                  </header>
                  {description && (
                    <BaseDialog.Description {...stylex.props(styles.description)}>{description}</BaseDialog.Description>
                  )}
                </div>
                <div {...stylex.props(styles.body)}>{children}</div>
              </>
            )}
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  )
}
